import { App, areAppTypesEquivalent, isTierZuJungByToday, Wartezeitverzicht } from '@ps20-frontend/frontend-models';
import { Tierart } from '@ps20/bff-models';
import { AngebotsId } from '@ps20-frontend/shop-integration-redux';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE, isYoungerThan } from '@ps20-frontend/utils';
import {
  GegeCheckoutProdukt,
  Produkt,
  RsBaseProdukt,
  RsCheckoutProdukt,
  TkvCheckoutProdukt,
  UnfCheckoutProdukt,
  VhvCheckoutProdukt,
  VwgCheckoutProdukt,
} from '../model';
import { IAppState } from '../localStore';
import { memoizeOnArrayInput } from './selector.util';

function getProdukt(state: IAppState, appId: App): Produkt {
  return state.produkte.find(vd => vd.appId === appId);
}

function getRsProdukt(state: IAppState): RsCheckoutProdukt {
  return state.produkte.find(vd => vd.appId === App.RS) as RsCheckoutProdukt;
}

function getTkvProdukt(state: IAppState): TkvCheckoutProdukt {
  return state.produkte.find(
    vd => areAppTypesEquivalent(vd.appId, App.TKV),
  ) as TkvCheckoutProdukt;
}

function getTkvProduktWithSameAngebotsId(state: IAppState, angebotsId: string): TkvCheckoutProdukt {
  return state.produkte.find(
    vd => areAppTypesEquivalent(vd.appId, App.TKV) && vd.angebotsId === angebotsId,
  ) as TkvCheckoutProdukt;
}

export const showAmtlichesKennzeichen = (state: IAppState): boolean => {
  const rsProdukt: RsCheckoutProdukt = getRsProdukt(state);
  return rsProdukt && rsProdukt.hasEinFahrzeugVersichert;
};

export const getVhvCheckoutProdukt = (state: IAppState): Array<VhvCheckoutProdukt> =>
  state.produkte.filter(vd => areAppTypesEquivalent(vd.appId, App.VHV)) as VhvCheckoutProdukt[];

export const selectAmtlichesKennzeichen = (state: IAppState): string => {
  return getRsProdukt(state).amtlichesKennzeichen;
};

export const selectedProdukte = (state: IAppState): Array<Produkt> => {
  return state.produkte;
};

export const selectProdukteWithoutBeibehaltung = (state: IAppState): Array<Produkt> => {
  return state.produkte.filter(produkt => !produkt.beibehalten);
};

const memoizedVorvertragsnummern = memoizeOnArrayInput((produkte: Array<Produkt>) =>
  produkte
    .filter(produkt => !produkt.beibehalten)
    .flatMap(produkt => produkt.vorvertragsnummer ?? [])
    .map(vorvertrag => vorvertrag.vertragsnummer)
    .filter(Boolean),
);

export const selectVorvertragsnummern = (state: IAppState): Array<string> =>
  memoizedVorvertragsnummern(state.produkte);

export const getProdukteAsAngebotsIds = (state: IAppState): Array<AngebotsId> => {
  return state.produkte.map(produkt => ({
    appId: produkt.appId,
    angebotsId: produkt.angebotsId,
  }));
};

export const showWartezeitverzicht =
  (appId: App) =>
  (state: IAppState): boolean => {
    if (appId == App.RS) {
      const rsProdukt: RsCheckoutProdukt = getRsProdukt(state);
      return rsProdukt && rsProdukt.wartezeitverzicht.showWartezeitverzicht;
    }
    if (appId == App.RS_VERMIETUNG) {
      const rsVermietungProdukt: RsBaseProdukt = getProdukt(
        state,
        App.RS_VERMIETUNG,
      ) as RsBaseProdukt;
      return rsVermietungProdukt?.wartezeitverzicht.showWartezeitverzicht;
    }
    return false;
  };

export const selectWartezeitverzicht =
  (appId: App) =>
  (state: IAppState): Wartezeitverzicht => {
    return (getProdukt(state, appId) as RsBaseProdukt)?.wartezeitverzicht;
  };

export const showTkvDiagnosen = (state: IAppState, angebotsId: string): boolean => {
  const tkvProdukt: TkvCheckoutProdukt = getTkvProduktWithSameAngebotsId(state, angebotsId);
  return tkvProdukt?.inBehandlung;
};

export const selectTkvTierart = (state: IAppState): Tierart => {
  const tkvProdukt: TkvCheckoutProdukt = getTkvProdukt(state);
  return tkvProdukt?.tierart;
};

export const selectProdukt = (state: IAppState, angebotsId: AngebotsId): Produkt => {
  return state.produkte.find(
    produkt => produkt.appId === angebotsId.appId && produkt.angebotsId === angebotsId.angebotsId,
  );
};

export const selectJhpProdukt = (state: IAppState): Produkt =>
  selectProdukt(state, { appId: App.JHP });

export const selectContainsNotBeibehalteneJhpAngebot = (state: IAppState): boolean =>
  selectedProdukte(state).some(produkt => produkt.appId === App.JHP && !produkt.beibehalten);

export const showUnfAngaben = (state: IAppState): boolean =>
  selectNotBeibehalteneUnfProdukte(state).length > 0;

export const selectNotBeibehalteneUnfProdukte = (state: IAppState): UnfCheckoutProdukt[] =>
  selectedProdukte(state)
    .filter(produkt => produkt.appId === App.UNF)
    .filter(produkt => !produkt.beibehalten);

export const selectUnfallProdukteWithBezugsrecht = (state: IAppState): UnfCheckoutProdukt[] =>
  selectNotBeibehalteneUnfProdukte(state).filter(
    produkt => produkt.unfallVp.hasTodesfallleistung && !isVpMinor(produkt.unfallVp),
  );

const isVpMinor = (vp: UnfCheckoutProdukt['unfallVp']): boolean =>
  isYoungerThan(vp.geburtsdatum, dayjs(), 18);

export const selectUnfallProdukteWithoutKIS = (state: IAppState): UnfCheckoutProdukt[] =>
  selectNotBeibehalteneUnfProdukte(state).filter(produkt => produkt.unfallVp.hasKIS === false);

export const getGeburtsdatumTierWennZuJung = (geburtsdatumTier: string): string => {
  if (isTierZuJungByToday(geburtsdatumTier, DATEFORMAT_LOCAL_STATE)) {
    return geburtsdatumTier;
  }
  return undefined;
};

export const getGeburtsdatumVp = (produkt: UnfCheckoutProdukt): string => {
  return produkt.unfallVp.geburtsdatum;
};

export const showGegeAngaben = (state: IAppState): boolean =>
  selectedProdukte(state).some(produkt => produkt.appId === App.GEGE);

export const selectGegeProdukte = (state: IAppState): GegeCheckoutProdukt[] =>
  selectedProdukte(state).filter(produkt => produkt.appId === App.GEGE);

export const selectVWGProdukteWithoutBeibehaltung = (
  state: IAppState,
): Array<VwgCheckoutProdukt> => {
  const produkteWithoutBeibehaltung = selectProdukteWithoutBeibehaltung(state);
  return produkteWithoutBeibehaltung.filter(
    value => areAppTypesEquivalent(value.appId, App.VWG),
  ) as VwgCheckoutProdukt[];
};
